import React from "react";
import { Flex } from "antd";
import styled from "styled-components";
import { InfoTooltip, Skeleton, radius } from "@libertify/ui";
import { analyticsTheme } from "./analyticsTheme";

const LABEL_COL = 96;
const ROW_GAP = 12;

const StyledCard = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid ${analyticsTheme.cardBorder};
  border-radius: ${radius.box}px;
  padding: 20px 24px;
  background: ${analyticsTheme.cardBg};
`;

const Title = styled.span`
  font-size: 16px;
  font-weight: 600;
`;

const StepLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${analyticsTheme.textTitle};
  flex: 0 0 ${LABEL_COL}px;
  overflow-wrap: break-word;
  line-height: 1.3;
`;

const StyledInfoTooltip = styled(InfoTooltip)`
  margin-left: 4px;
  vertical-align: middle;
`;

const StepCount = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${analyticsTheme.textTitle};
  min-width: 24px;
  text-align: right;
`;

const BarContainer = styled.div`
  flex: 1;
  height: 24px;
  background: ${analyticsTheme.barTrack};
  border-radius: 4px;
  overflow: hidden;
`;

const BarFill = styled.div<{ $width: number; $color: string }>`
  height: 100%;
  width: ${({ $width }) => $width}%;
  background: ${({ $color }) => $color};
  border-radius: 4px;
  display: flex;
  align-items: center;
  padding: 0 8px;
  min-width: fit-content;
`;

const BarLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${analyticsTheme.textBarLabel};
  white-space: nowrap;
`;

const ConnectorText = styled.span`
  font-size: 11px;
  color: ${analyticsTheme.textMuted};
  padding-left: ${LABEL_COL + ROW_GAP}px;
`;

export interface FunnelStep {
  label: string;
  percentage: number;
  count: number;
  color: string;
  /** Optional explanation shown in a tooltip next to the label. */
  info?: string;
}

export interface FunnelConnector {
  text: string;
}

export interface FunnelCardProps {
  title: string;
  steps: FunnelStep[];
  connectors?: FunnelConnector[];
  loading?: boolean;
}

export const FunnelCard: React.FC<FunnelCardProps> = ({
  title,
  steps,
  connectors = [],
  loading = false,
}) => {
  if (loading) {
    return (
      <StyledCard>
        <Skeleton variant="rectangular" width="40%" height={18} />
        <Flex vertical gap={16} style={{ marginTop: 16 }}>
          <Skeleton variant="rectangular" width="100%" height={24} />
          <Skeleton variant="rectangular" width="80%" height={24} />
        </Flex>
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      <Title style={{ marginBottom: 16 }}>{title}</Title>
      <Flex vertical gap={4}>
        {steps.map((step, index) => (
          <React.Fragment key={step.label}>
            <Flex align="center" gap={ROW_GAP}>
              <StepLabel>
                {step.label}
                {step.info ? (
                  <StyledInfoTooltip content={step.info} />
                ) : null}
              </StepLabel>
              <BarContainer>
                <BarFill $width={step.percentage} $color={step.color}>
                  <BarLabel>{Math.round(step.percentage)}%</BarLabel>
                </BarFill>
              </BarContainer>
              <StepCount>{step.count}</StepCount>
            </Flex>
            {connectors[index] && (
              <ConnectorText>&darr; {connectors[index].text}</ConnectorText>
            )}
          </React.Fragment>
        ))}
      </Flex>
    </StyledCard>
  );
};
